import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import axios from "axios";
import { API_BASE } from "../config/api";
import {
  FaChalkboardTeacher,
  FaRunning,
  FaLanguage,
  FaShieldAlt,
  FaCalendarCheck,
  FaWhatsapp,
  FaCheckCircle,
  FaStar,
} from "react-icons/fa";

/* ===================== config ===================== */
const API = API_BASE;

/* ===================== motion helper ===================== */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut", delay },
});

/* ===================== static content ===================== */
const features = [
  {
    icon: <FaChalkboardTeacher className="text-4xl text-blue-500" />,
    title: "Top Experts & Teachers",
    description:
      "Find skilled coaches and teachers across fitness, languages, academics, music, and more — all in one place.",
  },
  {
    icon: <FaRunning className="text-4xl text-green-500" />,
    title: "Flexible Scheduling",
    description:
      "Experts publish availability and clients request sessions at times that fit both sides.",
  },
  {
    icon: <FaLanguage className="text-4xl text-purple-500" />,
    title: "Multiple Categories",
    description:
      "From strength to Spanish, from calculus to guitar — find or offer the perfect expertise.",
  },
  {
    icon: <FaCalendarCheck className="text-4xl text-indigo-500" />,
    title: "Effortless Bookings",
    description:
      "Requests land in your dashboard and via email/WhatsApp. Accept, cancel, and keep your calendar clear.",
  },
  {
    icon: <FaShieldAlt className="text-4xl text-rose-500" />,
    title: "Trust, Reviews & Safety",
    description:
      "Trust grows through real client reviews after sessions. Platform verification will be introduced later.",
  },
  {
    icon: <FaWhatsapp className="text-4xl text-emerald-500" />,
    title: "Direct Communication",
    description:
      "Chat fast and coordinate easily. No hidden hoops — just clear, direct communication.",
  },
];

const steps = [
  {
    step: "01",
    title: "Create your expert profile",
    text: "Add specialties, locations, pricing (or ‘Request Price’), and your gallery.",
  },
  {
    step: "02",
    title: "Set availability",
    text: "Publish time slots so clients can request sessions instantly — no back-and-forth.",
  },
  {
    step: "03",
    title: "Accept requests",
    text: "Approve in one tap. Accepted time slots are auto-blocked to prevent double-booking.",
  },
];

const testimonials = [
  {
    text:
      "PTfinderNow helped me find the perfect coach. It’s super fast and the booking flow is clean.",
    name: "Amine B.",
    rating: 5,
  },
  {
    text:
      "As a student, I saved so much time booking a private teacher. The calendar feature is gold.",
    name: "Sophia L.",
    rating: 5,
  },
];

const faqs = [
  {
    q: "Is there any upfront cost to join?",
    a:
      "No. Your public coach listing is free. Lead Access costs AED 199 monthly or AED 1,910.00 yearly (save 20%) when you choose to unlock client inquiries.",
  },
  {
    q: "Do I control my pricing?",
    a:
      "Yes — 100%. Set your price per session, or show “Request Price” if you prefer to quote after chatting with the client.",
  },
  {
    q: "How do bookings work?",
    a:
      "Clients request sessions based on your availability. You Accept or Cancel. When accepted, the slot is blocked automatically.",
  },
  {
    q: "How does trust work in the beginning?",
    a:
      "In pre-launch, reputation is built through real client reviews after sessions. Platform verification (manual review / ID checks) will be added later.",
  },
  {
    q: "Can I pause or leave anytime?",
    a:
      "Yes. You can hide your profile, pause availability, or cancel a plan anytime. No lock-in.",
  },
];

/* ===================== page ===================== */
export default function About() {
  // Live counters (robust to API shapes)
  const [ptCount, setPtCount] = React.useState(null);
  const [loadingCount, setLoadingCount] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/Coaches/Search`, {
          params: {},
        });
        if (!mounted) return;
        const data = res.data;

        const total =
          data?.total ??
          data?.count ??
          data?.Total ??
          data?.Count ??
          (Array.isArray(data) ? data.length : undefined) ??
          (Array.isArray(data?.items) ? data.items.length : undefined) ??
          (Array.isArray(data?.$values) ? data.$values.length : undefined) ??
          0;

        setPtCount(Number.isFinite(total) ? total : 0);
      } catch (e) {
        console.error("Failed to load PT count:", e);
        setPtCount(null);
      } finally {
        if (mounted) setLoadingCount(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // FAQ state (multi-open allowed)
  const [openFaq, setOpenFaq] = React.useState(new Set());
  const toggleFaq = (i) =>
    setOpenFaq((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 overflow-hidden">
      {/* HERO (mobile-optimized spacing & type) */}
      <section className="relative">
        <img
          src="https://images.unsplash.com/photo-1580894732444-8ecded7900cd?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
          alt="Personal training session"
          className="w-full h-[380px] sm:h-[460px] md:h-[520px] object-cover brightness-75"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
          <motion.h1
            {...fadeUp()}
            className="text-[28px] leading-tight sm:text-4xl md:text-6xl font-extrabold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-purple-300 drop-shadow"
          >
            Welcome to PTfinderNow
          </motion.h1>
          <motion.p
            {...fadeUp(0.12)}
            className="max-w-[640px] text-[15px] sm:text-lg md:text-xl text-white/95"
          >
            Your bridge to expert coaches and private teachers — anytime, anywhere.
          </motion.p>

          <motion.div
            {...fadeUp(0.22)}
            className="mt-6 flex flex-col sm:flex-row gap-3 w-full sm:w-auto px-4 sm:px-0"
          >
            <Link to="/signup" className="w-full sm:w-auto">
              <span className="inline-block w-full rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-6 sm:px-7 py-3 text-center font-semibold text-white shadow hover:opacity-95 transition">
                Join as an Expert
              </span>
            </Link>
            <Link to="/coaches/Search" className="w-full sm:w-auto">
              <span className="inline-block w-full rounded-full bg-white/10 px-6 sm:px-7 py-3 text-center font-semibold text-white ring-1 ring-white/30 backdrop-blur hover:bg-white/15 transition">
                Find an Expert
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS (live PT count) */}
      <section className="py-12 sm:py-14 flex flex-wrap items-center justify-center gap-10 sm:gap-12 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-gray-800 dark:to-gray-700">
        <motion.div {...fadeUp()} className="text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-blue-600 dark:text-blue-400">
            {loadingCount ? (
              <span className="inline-block h-8 w-20 animate-pulse rounded bg-blue-200/60 dark:bg-blue-900/40" />
            ) : (
              <CountUp end={ptCount ?? 500} duration={2.2} />
            )}
            +
          </h2>
          <p className="mt-1 sm:mt-2 text-base sm:text-lg opacity-80">Active Experts</p>
        </motion.div>

        <motion.div {...fadeUp(0.08)} className="text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-green-500 dark:text-green-400">
            <CountUp end={1200} duration={2.2} />+
          </h2>
          <p className="mt-1 sm:mt-2 text-base sm:text-lg opacity-80">Clients Helped</p>
        </motion.div>

        <motion.div {...fadeUp(0.16)} className="text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-amber-500 dark:text-amber-400">
            4.9<span className="text-xl sm:text-2xl align-top">/5</span>
          </h2>
          <p className="mt-1 sm:mt-2 text-base sm:text-lg opacity-80">Avg. Satisfaction</p>
        </motion.div>
      </section>

      {/* FEATURES (tight mobile grid) */}
      <section className="py-14 sm:py-16 container mx-auto px-4 sm:px-6">
        <motion.h2
          {...fadeUp()}
          className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center mb-8 sm:mb-12"
        >
          Why Choose Us?
        </motion.h2>

        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              {...fadeUp(0.05 + index * 0.06)}
              className="bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-2xl shadow ring-1 ring-gray-100/80 dark:ring-gray-700/60 hover:shadow-xl transition"
            >
              <div className="flex justify-center mb-3 sm:mb-4">{feature.icon}</div>
              <h3 className="text-lg sm:text-xl font-semibold text-center mb-1.5 sm:mb-2">
                {feature.title}
              </h3>
              <p className="text-center text-[14px] sm:text-base text-gray-600 dark:text-gray-400">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-14 sm:py-16 bg-gray-100 dark:bg-gray-800">
        <motion.h2
          {...fadeUp()}
          className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center mb-8 sm:mb-12"
        >
          How It Works
        </motion.h2>
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-4 sm:gap-6 px-4 sm:px-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              {...fadeUp(0.05 + i * 0.06)}
              className="relative rounded-2xl bg-white dark:bg-gray-700 p-5 sm:p-6 shadow ring-1 ring-gray-100 dark:ring-gray-600"
            >
              <div className="absolute right-5 top-5 text-4xl sm:text-5xl font-black leading-none text-gray-100 dark:text-gray-600">
                {s.step}
              </div>
              <h5 className="text-lg font-semibold">{s.title}</h5>
              <p className="mt-1.5 sm:mt-2 text-[14px] sm:text-base text-gray-600 dark:text-gray-300">
                {s.text}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TRUST / REVIEWS / FUTURE VERIFICATION */}
      <section className="py-12 sm:py-14 container mx-auto px-4 sm:px-6">
        <motion.div
          {...fadeUp()}
          className="rounded-2xl bg-white dark:bg-gray-800 p-6 sm:p-8 shadow ring-1 ring-gray-100 dark:ring-gray-700"
        >
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
            <div className="mt-1 text-rose-500">
              <FaShieldAlt className="text-3xl" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold">Trust & Reviews</h3>
              <p className="mt-2 text-[14px] sm:text-base text-gray-600 dark:text-gray-300">
                During pre-launch, trust is built through real client reviews after sessions.
                Platform verification (manual review / document checks) will be introduced later.
              </p>

              <ul className="mt-3 sm:mt-4 grid gap-2 text-gray-700 dark:text-gray-200 text-[14px] sm:text-base">
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-500" />
                  Reviews come from real clients after sessions.
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-500" />
                  Clear booking flow helps prevent double-booking.
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-500" />
                  Experts with strong reviews naturally rank higher over time.
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-14 sm:py-16 container mx-auto px-4 sm:px-6">
        <motion.h2
          {...fadeUp()}
          className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center mb-8 sm:mb-12"
        >
          What Our Users Say
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              {...fadeUp(0.05 + i * 0.06)}
              className="bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-2xl shadow ring-1 ring-gray-100 dark:ring-gray-700"
            >
              <div className="flex items-center gap-2 text-amber-500 mb-3">
                {Array.from({ length: t.rating }).map((_, idx) => (
                  <FaStar key={idx} />
                ))}
              </div>
              <p className="italic mb-4">“{t.text}”</p>
              <h4 className="font-semibold text-right">— {t.name}</h4>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ (inline accordion) */}
      <section className="py-14 sm:py-16 container mx-auto px-4 sm:px-6">
        <motion.h2
          {...fadeUp()}
          className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center mb-6"
        >
          FAQ — For Experts
        </motion.h2>
        <p className="text-center text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-[14px] sm:text-base">
          Straight answers so you can focus on what you do best.
        </p>

        <div className="mt-6 sm:mt-8 space-y-3 max-w-4xl mx-auto">
          {faqs.map((item, i) => {
            const isOpen = openFaq.has(i);
            return (
              <div
                key={i}
                className="group rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-5 shadow ring-1 ring-gray-100 dark:ring-gray-700 transition hover:shadow-lg"
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  onClick={() => toggleFaq(i)}
                  className="flex w-full items-center gap-3 text-left"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                    <FaCheckCircle />
                  </div>
                  <span className="flex-1 text-[15px] sm:text-lg font-semibold">
                    {item.q}
                  </span>
                  <span
                    className={`text-sm font-semibold transition ${
                      isOpen ? "text-indigo-600" : "text-gray-500"
                    }`}
                  >
                    {isOpen ? "—" : "+"}
                  </span>
                </button>

                <motion.div
                  id={`faq-panel-${i}`}
                  role="region"
                  initial={false}
                  animate={{
                    height: isOpen ? "auto" : 0,
                    opacity: isOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.24, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="pl-[3.75rem] pr-1 pt-3 text-[14px] sm:text-base text-gray-600 dark:text-gray-300">
                    {item.a}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA (stacked on mobile) */}
      <section className="py-14 sm:py-16 bg-blue-700 text-white text-center">
        <motion.div
          {...fadeUp()}
          className="space-y-5 sm:space-y-6 px-4 sm:px-6 max-w-3xl mx-auto"
        >
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold">
            Join PTfinderNow Today!
          </h2>
          <p className="text-[15px] sm:text-lg md:text-xl text-white/90">
            Find an expert — or start getting clients. Free to start and takes just a minute.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" className="w-full sm:w-auto">
              <span className="inline-block w-full bg-white text-blue-700 font-semibold py-3 px-8 rounded-full hover:bg-gray-100 transition">
                Become an Expert
              </span>
            </Link>
            <Link to="/coaches/Search" className="w-full sm:w-auto">
              <span className="inline-block w-full bg-blue-600/20 ring-1 ring-white/30 text-white font-semibold py-3 px-8 rounded-full hover:bg-blue-600/30 transition">
                Explore Experts
              </span>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
